import { useState, useCallback } from 'react';
import { dismissNudge } from '../utils/api';

/**
 * useDismissedNudges — server-backed nudge dismissal (Day 50 rewrite).
 *
 * Previously localStorage with a 7-day TTL (#185), which caused dismissed
 * nudges to reappear across devices/sessions and after 7 days. Now each
 * dismissal is POSTed to /users/me/nudges/dismiss and persisted permanently
 * in nudge_dismissals; the server (computeNudgesForUser) filters them out on
 * the next fetch. An in-memory Set provides instant optimistic hiding within
 * the current session; callers should refetch nudges after dismissing so the
 * server state becomes authoritative.
 *
 * The `namespace` parameter is retained for call-site compatibility but is
 * now ignored — dismissal is global per user, server-side.
 *
 * Keys MUST be the canonical nudge key (category:tripId||'global'); use
 * nudgeKeyFor() from utils/api to build them consistently.
 */
export const useDismissedNudges = (_namespace?: string) => {
    const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(() => new Set());

    const isDismissed = useCallback(
        (key: string) => dismissedKeys.has(key),
        [dismissedKeys]
    );

    const dismiss = useCallback((key: string, triggerState = '') => {
        // Optimistic hide
        setDismissedKeys(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
        // Persist server-side (fire-and-forget; failure logged, optimistic state kept
        // until next refetch which is authoritative)
        dismissNudge(key, triggerState).catch(err => {
            console.warn('dismissNudge failed:', err);
        });
    }, []);

    const dismissAll = useCallback((keys: string[]) => {
        setDismissedKeys(prev => {
            const next = new Set(prev);
            for (const k of keys) next.add(k);
            return next;
        });
        for (const k of keys) {
            dismissNudge(k).catch(err => console.warn('dismissNudge failed:', err));
        }
    }, []);

    return { isDismissed, dismiss, dismissAll };
};
